// AppointmentCallModal.js — Call/WhatsApp record modal for appointments
// Shows call status, templates, notes, and 3 save buttons like KIT LeadModal

import React, { useState } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';

var CALL_STATUSES = [
  { key: 'successful',   label: 'Call Successful',           color: '#16a34a', bg: '#dcfce7' },
  { key: 'no_answer',    label: 'No Answer',                 color: '#d97706', bg: '#fef3c7' },
  { key: 'busy',         label: 'Busy / Call Back Later',    color: '#ea580c', bg: '#fff7ed' },
  { key: 'unreachable',  label: 'Switched Off / Unreachable',color: '#dc2626', bg: '#fef2f2' },
  { key: 'wrong_number', label: 'Wrong Number',              color: '#7c3aed', bg: '#f5f3ff' },
];

var TEMPLATES_BY_STATUS = {
  successful: [
    'Patient confirmed appointment',
    'Discussed treatment progress',
    'Patient wants to reschedule',
    'Payment reminder given',
    'Patient will resume sessions',
  ],
  no_answer: [
    'Will try again later',
    'Left voicemail / message',
  ],
  busy: [
    'Patient asked to call back',
    'Patient is travelling',
    'Patient will call us back',
  ],
  unreachable: [
    'Phone switched off, will try tomorrow',
    'Number not reachable',
  ],
  wrong_number: [
    'Number is incorrect / not in service',
  ],
};

export default function AppointmentCallModal({ appointment, phone, mode, onClose, onSaved, onSchedule }) {
  var { auth } = useAuth();
  var _s = useState(null),      callStatus = _s[0],      setCallStatus = _s[1];
  var _t = useState(''),        template = _t[0],        setTemplate = _t[1];
  var _n = useState(''),        notes = _n[0],           setNotes = _n[1];
  var _sv = useState(false),    saving = _sv[0],         setSaving = _sv[1];
  var _rd = useState(''),       reminderDate = _rd[0],   setReminderDate = _rd[1];
  var _sr = useState(false),    showReminder = _sr[0],   setShowReminder = _sr[1];
  var _ca = useState(false),    callMade = _ca[0],       setCallMade = _ca[1];

  // Extract patient name from appointment
  var patientName = appointment.name || (appointment.title || '').split('/')[0].trim() || 'Patient';

  // Default reminder date = today + 2 days
  function getDefaultReminder() {
    var d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  }

  function handleMakeCall() {
    setCallMade(true);
    if (mode === 'whatsapp') {
      window.open('https://wa.me/91' + phone, '_blank');
    } else {
      window.open('tel:+91' + phone, '_blank');
    }
  }

  async function saveRecord(action) {
    if (!callStatus) { alert('Please select a call status'); return; }
    setSaving(true);
    try {
      var summary = [template, notes].filter(Boolean).join(' — ');
      await api.put(process.env.REACT_APP_URL + '/appointments/' + appointment._id + '/callrecord', {
        mode: mode,
        status: callStatus,
        template: template,
        notes: notes,
        loggedBy: auth?.username || '',
      });

      // Create reminder task if "Save & Next Reminder"
      if (action === 'reminder') {
        var rd = reminderDate || getDefaultReminder();
        await api.post(process.env.REACT_APP_URL + '/tasks', {
          task: {
            title: 'Follow up: ' + patientName + (appointment.patientCard ? ' (' + appointment.patientCard + ')' : ''),
            description: 'Call follow-up — ' + (CALL_STATUSES.find(function(s) { return s.key === callStatus; }) || {}).label + (summary ? ': ' + summary : ''),
            assignee: auth?.username || appointment.username,
            createdBy: auth?.username || 'system',
            frequency: 'OneTime',
            status: 'new',
            urgentLevel: 'Important',
            estimatedEndDate: new Date(rd + 'T23:59:59'),
          }
        });
      }

      if (action === 'schedule' && onSchedule) {
        onSchedule(appointment);
      } else if (onSaved) {
        onSaved();
      }
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  }

  var templates = callStatus ? (TEMPLATES_BY_STATUS[callStatus] || []) : [];
  var statusObj = CALL_STATUSES.find(function(s) { return s.key === callStatus; });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: '-webkit-flex', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 12,
    }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420,
        maxHeight: '90vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 18px', borderRadius: '16px 16px 0 0',
          background: mode === 'whatsapp' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#2563eb,#1d4ed8)',
          color: '#fff',
        }}>
          <div style={{ display: '-webkit-flex', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{mode === 'whatsapp' ? '\uD83D\uDCAC WhatsApp' : '\uD83D\uDCDE Call'} — {patientName}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>+91 {phone}</div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 30, height: 30, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>x</button>
          </div>
        </div>

        <div style={{ padding: '16px 18px' }}>
          {/* Step 1: Make the call */}
          {!callMade ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                Tap below to {mode === 'whatsapp' ? 'open WhatsApp' : 'make the call'}. Come back here to log the result.
              </div>
              <button onClick={handleMakeCall} style={{
                padding: '14px 28px', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer', color: '#fff',
                background: mode === 'whatsapp' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }}>
                {mode === 'whatsapp' ? '\uD83D\uDCAC Open WhatsApp' : '\uD83D\uDCDE Make the Call'}
              </button>
              <div style={{ marginTop: 16 }}>
                <button onClick={function() { setCallMade(true); }} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                  Skip — already called
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Step 2: Call status */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>How did the call go?</div>
                <div style={{ display: '-webkit-flex', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CALL_STATUSES.map(function(s) {
                    var sel = callStatus === s.key;
                    return (
                      <button key={s.key} onClick={function() { setCallStatus(s.key); setTemplate(''); }}
                        style={{
                          padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          border: sel ? '2px solid ' + s.color : '1.5px solid #e2e8f0',
                          background: sel ? s.bg : '#f8fafc',
                          color: sel ? s.color : '#64748b',
                        }}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 3: Templates based on status */}
              {callStatus && templates.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Quick summary</div>
                  <div style={{ display: '-webkit-flex', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {templates.map(function(t) {
                      var sel = template === t;
                      return (
                        <button key={t} onClick={function() { setTemplate(sel ? '' : t); }}
                          style={{
                            padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                            border: sel ? '2px solid ' + (statusObj?.color || '#3b82f6') : '1.5px solid #e2e8f0',
                            background: sel ? (statusObj?.bg || '#eff6ff') : '#fff',
                            color: sel ? (statusObj?.color || '#1d4ed8') : '#334155',
                          }}>
                          {sel ? '\u2713 ' : ''}{t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 4: Extra notes */}
              {callStatus && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Additional notes</div>
                  <textarea
                    value={notes}
                    onChange={function(e) { setNotes(e.target.value); }}
                    placeholder="Any extra details..."
                    rows={2}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                      fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}

              {/* Reminder date picker (shown when Save & Reminder clicked) */}
              {showReminder && (
                <div style={{ marginBottom: 14, padding: 12, background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>Reminder date</div>
                  <input type="date" value={reminderDate || getDefaultReminder()}
                    onChange={function(e) { setReminderDate(e.target.value); }}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #7dd3fc', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                  />
                  <button onClick={function() { saveRecord('reminder'); }} disabled={saving}
                    style={{
                      marginTop: 8, width: '100%', padding: '10px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      background: '#0284c7', color: '#fff',
                      opacity: saving ? 0.6 : 1,
                    }}>
                    {saving ? 'Saving...' : '\u23F0 Save & Set Reminder'}
                  </button>
                </div>
              )}

              {/* Three action buttons */}
              {callStatus && !showReminder && (
                <div style={{ display: '-webkit-flex', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
                  <button onClick={function() { saveRecord('save'); }} disabled={saving}
                    style={{
                      padding: '12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      background: '#f8fafc', color: '#334155',
                      opacity: saving ? 0.6 : 1,
                    }}>
                    {saving ? 'Saving...' : '\uD83D\uDCBE Save'}
                  </button>
                  <button onClick={function() { saveRecord('schedule'); }} disabled={saving}
                    style={{
                      padding: '12px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff',
                      opacity: saving ? 0.6 : 1,
                    }}>
                    {saving ? 'Saving...' : '\uD83D\uDCC5 Save & Mark Next Appointment'}
                  </button>
                  <button onClick={function() { setShowReminder(true); setReminderDate(getDefaultReminder()); }} disabled={saving}
                    style={{
                      padding: '12px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      background: 'linear-gradient(135deg,#0284c7,#0369a1)', color: '#fff',
                      opacity: saving ? 0.6 : 1,
                    }}>
                    {'\u23F0 Save & Give Me Next Reminder'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
